<script setup lang="ts">
interface CardClubProps {
  /**
   * The title of the card.
   */
  title?: string;

  /**
   * The URL of the image to be displayed on the card.
   */
  imageUrl?: string;

  /**
   * Indicates whether the card should display a follow button.
   */
  hasFollowButton?: boolean;

  /**
   * Indicates whether the user is currently following the content of the card.
   */
  isFollowing?: boolean;

  /**
   * Indicates whether the follow button is disabled.
   */
  isFollowingDisabled?: boolean;

  /**
   * Determines whether to display initials on the image if no image URL is provided.
   */
  withInitials?: boolean;

  /**
   * The width of the card image.
   */
  width?: number;

  /**
   * The height of the card image.
   */
  height?: number;
}
const { t } = useI18n();
const color = useColorMode();
const props = withDefaults(defineProps<CardClubProps>(), {
  width: 96,
  height: 96,
});
const emit = defineEmits(["follow"]);
const onFollowClick = () => emit("follow");
</script>

<template>
  <div v-motion-pop class="cardClub justify-center">
    <div class="pt-5 pb-4">
      <div class="cardClub__image" :class="`w-[${props.width}px] h-[${props.height}px]`">
        <BaseLogo
          class="relative z-10"
          :src="props.imageUrl"
          :alt="props.title"
          :title="props.title"
          :width="props.width"
          :height="props.height"
          :with-initials="props.withInitials"
          with-border
          :no-background="color.value !== 'dark'"
        />
      </div>

      <div class="flex w-full items-center justify-center">
        <p class="text-center text-[var(--base-secondary-text-color)] w-[80%] mt-3 h-12 line-clamp-2" :title="props.title">
          {{ props.title }}
        </p>
      </div>
    </div>

    <div v-if="props.hasFollowButton" class="text-center h-10 flex justify-center items-center border-t border-[var(--base-background-color)]">
      <BaseButton
        class="text-sm w-full h-full"
        name="follow-club"
        link
        :type="props.isFollowing ? 'secondary' : 'primary'"
        :disabled="props.isFollowingDisabled"
        @click.prevent="onFollowClick()"
      >
        {{ props.isFollowing ? t('labels.following') : t('labels.follow') }}
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss">
.cardClub {
  @apply relative flex flex-col  max-w-[207px] flex-1
  overflow-hidden transition duration-100 hover:shadow-lg h-[237px];

  background-color: var(--base-card-bgColor);
  border-radius: var(--base-border-radius);

  .cardClub__image {
    @apply relative mx-auto rounded-full overflow-hidden
    flex items-center justify-center;
  }
}
</style>
